import type { Dispatch, SetStateAction } from 'react';
import type { Doc } from 'yjs';
import type { FormApi } from '../../form-hooks/useFormApi';
import type { SkippedContentTypeRefreshField } from '../../form/ContentObjectForm/helpers/contentTypeRefreshSnapshot';
import type {
  ContentObject,
  ContentTypeDefinition,
  WithOptionalInternalProps,
} from '../../lib/flotiq-client/types/api-response';

import { useCallback, useEffect, useReducer, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';

import { useModals } from '../../contexts/ModalContext';

import { fetchContentTypeRefreshPlan } from '../../form/ContentObjectForm/helpers/contentTypeRefreshSnapshot';
import useContentTypeUpdateMonitor from '../useContentTypeUpdateMonitor';
import useSelectedSpace from '../useSelectedSpace';
import useToken from '../useToken';
import { dropIncompatibleYjsValues } from './helpers/dropIncompatibleYjsValues';
import { initialRefreshState, refreshReducer } from './helpers/refreshReducer';
import { syncContentTypeUpdatedAt } from './helpers/syncContentTypeUpdatedAt';
import ContentTypeUpdatedModalContent from './modal/ContentTypeUpdatedModalContent';

/**
 * Refreshes an open object form when its content type definition changes
 * elsewhere. Detects staleness via useContentTypeUpdateMonitor — either from a
 * collaboration/WS signal or by polling — prompts the user, then remounts the
 * form on the new schema and restores the values that still fit.
 */

type ContentTypeRefreshOptions = {
  apiContentType?: ContentTypeDefinition | null;
  contentObject?: WithOptionalInternalProps<ContentObject> | null;
  contentTypeName?: string;
  form: {
    state?: FormApi;
    setState: Dispatch<SetStateAction<FormApi | undefined>>;
    setUniqueKey: Dispatch<SetStateAction<string>>;
  };
  collaboration: {
    yjsDocument?: Doc;
    wsStatus: string;
    contentTypeUpdate: { contentTypeName: string; updatedAt: string } | null;
  };
  mode: { id?: string; duplicate: boolean };
};

const useContentTypeRefresh = ({
  apiContentType,
  contentObject,
  contentTypeName,
  form,
  collaboration,
  mode,
}: ContentTypeRefreshOptions) => {
  const { t } = useTranslation();
  const modal = useModals();
  const jwt = useToken();
  const { space } = useSelectedSpace();

  const { state: formState, setState: setFormState, setUniqueKey } = form;
  const { yjsDocument, wsStatus, contentTypeUpdate } = collaboration;
  const { id, duplicate } = mode;
  const enabled = !!id && !duplicate;

  const [state, dispatch] = useReducer(refreshReducer, initialRefreshState);
  const formContentType = state.refreshedContentType || apiContentType;

  const refs = useRef({ formState, contentObject, formContentType });
  useEffect(() => {
    refs.current = { formState, contentObject, formContentType };
  });

  useEffect(() => {
    dispatch({ type: 'reset' });
  }, [contentTypeName]);

  useEffect(() => {
    if (!enabled || wsStatus !== 'synced' || !yjsDocument) return;
    syncContentTypeUpdatedAt(yjsDocument, formContentType?.updatedAt);
  }, [formContentType?.updatedAt, enabled, yjsDocument, wsStatus]);

  useEffect(() => {
    if (
      state.phase !== 'restoring' ||
      !state.pendingCompatibleValues ||
      !formState
    )
      return;

    const fields = Object.keys(state.pendingCompatibleValues);
    if (fields.length) {
      formState.setValues(state.pendingCompatibleValues as any);
      fields.forEach((field) => formState.setFieldTouched(field, true));
      void formState.validateForm('dynamic');
    }
    dispatch({ type: 'restored' });
  }, [formState, state.phase, state.pendingCompatibleValues]);

  const applyRefresh = useCallback(
    (
      nextContentType: ContentTypeDefinition,
      compatibleValues: Record<string, unknown>,
      incompatibleFields: SkippedContentTypeRefreshField[],
    ) => {
      dropIncompatibleYjsValues(yjsDocument, incompatibleFields);
      setFormState(undefined);
      dispatch({
        type: 'apply',
        contentType: nextContentType,
        compatibleValues,
      });
      setUniqueKey(`ctd-refresh-${nextContentType.updatedAt || Date.now()}`);
    },
    [setFormState, setUniqueKey, yjsDocument],
  );

  const handleOutdatedContentType = useCallback(async () => {
    dispatch({ type: 'detected' });
    if (modal.isOpen('content-type-updated')) return;

    const {
      formState: fs,
      contentObject: co,
      formContentType: ct,
    } = refs.current;

    let plan = null;
    if (contentTypeName && ct && fs) {
      try {
        plan = await fetchContentTypeRefreshPlan({
          jwt,
          space,
          contentTypeName,
          oldContentType: ct,
          formState: fs,
          contentObject: co,
        });
      } catch {
        toast.error(t('Form.CommunicationErrorMessage'));
      }
    }

    modal({
      id: 'content-type-updated',
      hideClose: true,
      title: (
        <div className="font-bold text-3xl">
          <h3>
            <Trans i18nKey="ContentForm.ContentTypeUpdatedTitle" />
          </h3>
        </div>
      ),
      content: (
        <ContentTypeUpdatedModalContent
          skippedFields={plan?.skippedFields ?? []}
        />
      ),
      buttons: [
        {
          key: 'reload',
          label: t('ContentForm.Reload'),
          result: true,
          onClick: () => {
            if (!plan) {
              window.location.reload();
              return;
            }
            applyRefresh(
              plan.nextContentType,
              plan.compatibleValues,
              plan.incompatibleFields,
            );
          },
        },
      ],
      size: 'md',
      dialogAdditionalClasses: 'text-center',
    });
  }, [applyRefresh, contentTypeName, jwt, modal, space, t]);

  useContentTypeUpdateMonitor(formContentType, {
    enabled,
    updatedAt:
      contentTypeUpdate?.contentTypeName === formContentType?.name
        ? (contentTypeUpdate?.updatedAt ?? null)
        : null,
    onOutdated: handleOutdatedContentType,
  });

  return {
    formContentType,
    isContentTypeOutdated: state.phase !== 'idle',
  };
};

export default useContentTypeRefresh;
